import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { KeyRound, Loader2, Check, X, Eye, EyeOff, ArrowLeft, LogOut } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { apiError } from '../lib/projects';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';

const RULES = [
  { key: 'len', label: 'At least 10 characters', test: (p) => p.length >= 10 },
  { key: 'upper', label: 'An uppercase letter', test: (p) => /[A-Z]/.test(p) },
  { key: 'lower', label: 'A lowercase letter', test: (p) => /[a-z]/.test(p) },
  { key: 'digit', label: 'A digit', test: (p) => /\d/.test(p) },
];

const PasswordInput = ({ id, value, onChange, testId, autoComplete }) => {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <Input id={id} type={show ? 'text' : 'password'} value={value} onChange={(e) => onChange(e.target.value)} required
        autoComplete={autoComplete} className="pr-10" data-testid={testId} />
      <button type="button" onClick={() => setShow((s) => !s)} aria-label={show ? 'Hide password' : 'Show password'}
        className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" data-testid={`${testId}-toggle`}>
        {show ? <EyeOff size={15} /> : <Eye size={15} />}
      </button>
    </div>
  );
};

/**
 * Self-service password change. Also the forced first-login screen for credentials issued
 * from the Admin Panel (must_change_password). Rendered without the app shell so a forced
 * user cannot wander off before completing it.
 */
export default function ChangePasswordPage() {
  const { user, mustChangePassword, refreshSession, logout } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);

  const checks = useMemo(() => RULES.map((r) => ({ ...r, ok: r.test(next) })), [next]);
  const strong = checks.every((c) => c.ok);
  const matches = next.length > 0 && next === confirm;
  const different = next !== current;

  const submit = async (e) => {
    e.preventDefault();
    if (!strong || !matches || !different) return;
    setBusy(true);
    try {
      await api.post('/auth/change-password', { current_password: current, new_password: next });
      toast.success('Password updated. Other sessions were signed out.');
      await refreshSession();
      navigate('/', { replace: true });
    } catch (err) {
      toast.error(apiError(err, 'Could not change the password'));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4"
      style={{ background: 'radial-gradient(600px at 0% 0%, rgba(59,130,246,0.10), transparent), radial-gradient(600px at 100% 100%, rgba(99,102,241,0.10), transparent), hsl(210 40% 99%)' }}
      data-testid="change-password-page">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-6">
          <div className="h-14 w-14 rounded-2xl bg-white border border-border/60 flex items-center justify-center mb-3 shadow-lg shadow-primary/10">
            <KeyRound size={24} className="text-primary" />
          </div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">{mustChangePassword ? 'Set your new password' : 'Change password'}</h1>
          <p className="text-sm text-muted-foreground mt-1 text-center max-w-sm" data-testid="change-password-intro">
            {mustChangePassword
              ? `Welcome${user?.name ? `, ${user.name.split(' ')[0]}` : ''}. Your temporary password must be replaced before you continue.`
              : 'Choose a strong password. Every other session will be signed out.'}
          </p>
        </div>
        <Card className="shadow-xl shadow-slate-200/60 border-border/70">
          <CardContent className="pt-6">
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="current">{mustChangePassword ? 'Temporary password' : 'Current password'}</Label>
                <PasswordInput id="current" value={current} onChange={setCurrent} testId="change-password-current" autoComplete="current-password" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="next">New password</Label>
                <PasswordInput id="next" value={next} onChange={setNext} testId="change-password-new" autoComplete="new-password" />
                <ul className="grid grid-cols-2 gap-x-3 gap-y-0.5 pt-1" data-testid="change-password-rules">
                  {checks.map((c) => (
                    <li key={c.key} className={`flex items-center gap-1 text-[11px] ${c.ok ? 'text-emerald-700' : 'text-muted-foreground'}`} data-testid={`change-password-rule-${c.key}`}>
                      {c.ok ? <Check size={11} /> : <X size={11} />} {c.label}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="confirm">Confirm new password</Label>
                <PasswordInput id="confirm" value={confirm} onChange={setConfirm} testId="change-password-confirm" autoComplete="new-password" />
                {confirm && !matches && <p className="text-[11px] text-red-600" data-testid="change-password-mismatch">Passwords do not match</p>}
                {next && !different && <p className="text-[11px] text-red-600" data-testid="change-password-same">New password must differ from the current one</p>}
              </div>
              <Button type="submit" className="w-full" disabled={busy || !strong || !matches || !different} data-testid="change-password-submit">
                {busy ? <Loader2 className="animate-spin" size={16} /> : 'Update password'}
              </Button>
            </form>
            <div className="mt-5 flex items-center justify-between text-xs">
              {mustChangePassword ? (
                <button type="button" onClick={() => { logout(); navigate('/login'); }} className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground" data-testid="change-password-signout">
                  <LogOut size={12} /> Sign out instead
                </button>
              ) : (
                <Link to="/" className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground" data-testid="change-password-back"><ArrowLeft size={12} /> Back to apps</Link>
              )}
              <span className="text-muted-foreground" data-testid="change-password-email">{user?.email}</span>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
